import React, { FunctionComponent } from "react";
import "./SubscribedUser.scss";
import Table from "../../Components/Table/Table";
import CustomToolbar from "../../Components/Table/components/CustomToolbar";
import TableSearch from "../../Components/Table/components/TableSearch";
import { Columns } from "../../Components/Table/ITable";
import { Button, ButtonGroup, Menu, MenuItem, CircularProgress, Box } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import DownloadIcon from "@mui/icons-material/Download";
import * as XLSX from "xlsx";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Hooks";
import { useEffect } from "react";
import { listSubscribedUser } from "../../Redux/Slices/Package/ListSubscribedUserSlice";
import dayjs from "dayjs";


import { ISubscribedUser } from "./ISubscribedUser";

interface DownloadButtonProps {
    data: any[];
}

const DownloadButton: FunctionComponent<DownloadButtonProps> = ({ data }) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleDownloadExcel = () => {
        // Prepare data for Excel (flattened)
        const exportData = data.map((row) => ({
            "S.No": row.index,
            Username: row.username,
            Email: row.email,
            "Year Group": row.year_group,
            Package: row.package_name,
            "Start Date": row.subscribed_on,
            "End Date": row.end_date,
            "Pending Days": row.pending_days,
            Price: row.amount,
            Status: row.status ? "Active" : "Inactive",
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Subscribed Users");
        XLSX.writeFile(workbook, "SubscribedUsers.xlsx");
        handleClose();
    };

    const handleDownloadCSV = () => {
        const exportData = data.map((row) => ({
            "S.No": row.index,
            Username: row.username,
            Email: row.email,
            "Year Group": row.year_group,
            Package: row.package_name,
            "Start Date": row.subscribed_on,
            "End Date": row.end_date,
            "Pending Days": row.pending_days,
            Price: row.amount,
            Status: row.status ? "Active" : "Inactive",
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const csv = XLSX.utils.sheet_to_csv(worksheet);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute("href", url);
            link.setAttribute("download", "SubscribedUsers.csv");
            link.style.visibility = "hidden";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
        handleClose();
    };



    return (
        <React.Fragment>
            <ButtonGroup
                variant="contained"
                aria-label="split button"
                sx={{ height: "30px" }}
            >
                <Button
                    onClick={handleDownloadExcel}
                    startIcon={<DownloadIcon />}
                    sx={{
                        backgroundColor: "#009dff",
                        color: "#fff",
                        "&:hover": { backgroundColor: "#64b5f6" },
                    }}
                >
                    Download
                </Button>
                <Button
                    size="small"
                    aria-controls={open ? "split-button-menu" : undefined}
                    aria-expanded={open ? "true" : undefined}
                    aria-label="select merge strategy"
                    aria-haspopup="menu"
                    onClick={handleClick}
                    sx={{
                        color: "#fff",
                        "&:hover": { backgroundColor: "#64b5f6" },
                    }}
                >
                    <ArrowDropDownIcon sx={{ color: "#fff" }} />
                </Button>
            </ButtonGroup>
            <Menu
                id="split-button-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    "aria-labelledby": "split-button-menu",
                }}
            >
                <MenuItem onClick={handleDownloadCSV}>Export as CSV</MenuItem>

            </Menu>
        </React.Fragment>
    );
};

const SubscribedUser: FunctionComponent = () => {
    const dispatch = useAppDispatch();
    const { data: subscribedUsers, isLoading } = useAppSelector((state) => state.ListSubscribedUserSlice);

    useEffect(() => {
        dispatch(listSubscribedUser());
    }, [dispatch]);

    // 2. Columns Definition
    const columns: Columns = [
        {
            field: "index",
            renderHeader: () => <b>S.No</b>,
            width: 70,
            headerAlign: "center",
            align: "center",
            valueGetter: (params: any) => params.row.index,
        },
        {
            field: "username",
            renderHeader: () => <b>Username</b>,
            flex: 1,
            type: "string",
            headerAlign: "left",
        },
        {
            field: "email",
            renderHeader: () => <b>Email</b>,
            flex: 1.5,
            type: "string",
            headerAlign: "left",
        },
        {
            field: "year_group",
            renderHeader: () => <b>Year Group</b>,
            flex: 1,
            type: "string",
            headerAlign: "left",
        },
        {
            field: "package_name",
            renderHeader: () => <b>Package</b>,
            flex: 1,
            type: "string",
            headerAlign: "left",
        },
        {
            field: "subscribed_on",
            renderHeader: () => <b>Start Date</b>,
            flex: 1,
            type: "string",
            headerAlign: "left",
        },
        {
            field: "end_date",
            renderHeader: () => <b>End Date</b>,
            flex: 1,
            type: "string",
            headerAlign: "left",
        },
        {
            field: "pending_days",
            renderHeader: () => <b>Pending Days</b>,
            flex: 1,
            type: "number",
            headerAlign: "center",
            align: "center",
        },
        {
            field: "amount",
            renderHeader: () => <b>Price</b>,
            flex: 1,
            type: "number",
            headerAlign: "center",
            align: "center",
        },
        {
            field: "status",
            renderHeader: () => <b>Status</b>,
            flex: 1,
            type: "string",
            headerAlign: "left",
            cellClassName: (params) =>
                `activename ${params.row.status ? "active" : "inactive"}`,
            valueFormatter: (params) => (params.value ? "Active" : "Inactive"),
        },
    ];

    // Prepare rows with index and flatten data for grid functionality (sorting/filtering)
    const dataToMap = subscribedUsers?.data || [];
    const rows = dataToMap.map((item: any, idx: number) => {
        const today = dayjs();
        const endDate = dayjs(item.end_date);
        const diffInDays = endDate.diff(today, "day");

        return {
            ...item,
            id: item.sub_id,
            index: idx + 1,
            username: item.user_details?.username || "-",
            email: item.user_details?.email || "-",
            year_group: item.user_details?.year_group || "-",
            package_name: item.package_details?.name || "-",
            subscribed_on: item.subscribed_on ? dayjs(item.subscribed_on).format("DD-MM-YY") : "-",
            end_date: item.end_date ? dayjs(item.end_date).format("DD-MM-YY") : "-",
            amount: item.price_details?.amount || 0,
            pending_days: diffInDays > 0 ? diffInDays : 0,
        };
    });

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <>
            <Table rows={rows} columns={columns}>
                <CustomToolbar>
                    <div style={{ marginRight: 'auto' }}>
                        {/* Empty spacer or Title if needed */}
                    </div>
                    <DownloadButton data={rows} />
                    <TableSearch />
                </CustomToolbar>
            </Table>
        </>
    );
};

export default SubscribedUser;
